"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
import { Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
export default function Research() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const carouselRef = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const testimonials = [{
    quote: "NoMoCo products allow us to comfortably secure our subjects' heads for scanning, and greatly reduce the amount of data lost to motion artifacts. More importantly, the pillows increase comfort and encourage subjects to adopt a relaxed posture when participating in functional MRI scans. The various pillows also help to accommodate different head shapes and sizes.",
    name: "Lisa Marie Langevin PhD",
    role: "Alberta Children's Hospital Research Institute"
  }, {
    quote: "We have used the NoMoCo pillow for our Diffusion Tensor Imaging experiments at the Radiology Imaging Laboratories. We started our project without the pillows and noticed patient discomfort associated with table vibration due to specifics of our imaging protocol. We acquired the NoMoCo pillow and there was considerable patient satisfaction and improvement in comfort. In addition, support and follow up from the vendor have been exceptional, with prompt and professional response and resolution of any questions or issues.",
    name: "Mazyar E. Ahmadi MD",
    role: "UC San Diego Radiology"
  }, {
    quote: "I have used the NoMoCo pillow to help increase comfort and reduce motion in child, adolescent, and young adult research participants. Comfort and motion are critical design concerns with youth studies. After trying other, less effective techniques, I happily settled on the NoMoCo pillow system. Since then, not only have we seen less motion in our imaging data, but also noticeable improvements in participant comfort with scanning.",
    name: "Susan Tapert PhD",
    role: "UC San Diego Psychiatry"
  }, {
    quote: "We conduct sleep deprivation studies in older and younger adults. Subject motion in older populations, and in anyone who is sleep deprived, can be a serious problem. We have found that the comfort and stability afforded by the NoMoCo system have dramatically reduced the amount of data lost to excessive motion. In addition, the subjects consistently comment that the pillows are comfortable and our staff find them very easy to use.",
    name: "Sean P.A. Drummond PhD",
    role: "UCSD Lab of Sleep & Behavioral Neuroscience"
  }];
  return <section data-section-id="2810" id="research-development" className="text-foreground bg-primary ![padding:100px_24px_40px_24px]">
      {}
      <div ref={header.ref} className="max-w-5xl text-center transition-all duration-1000 ease-out opacity-100 translate-y-0 mx-auto">
        <p className="font-default text-xs tracking-[0.3em] uppercase mb-12 text-primary"><b className="text-accent">testimonials</b></p>
        <h2 className="font-default text-4xl md:text-6xl lg:text-[4.5rem] leading-[1.05] tracking-tight max-w-4xl mx-auto text-accent">
          We believe medical support is not a{" "}
          <em className="italic font-normal text-secondary">commodity</em>{" "}
          &mdash; it is a{" "}
          <em className="italic font-normal text-secondary">clinical necessity</em>
          .
        </h2>

        <div className="flex justify-center mt-16">
          <Separator className="w-24 bg-accent" />
        </div>
      </div>

      {}
      <div ref={carouselRef.ref} className={`max-w-6xl mx-auto transition-all duration-1000 ease-out ${carouselRef.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <Carousel opts={{
        align: "start",
        loop: true
      }} className="w-full max-w-5xl mx-auto">
          <CarouselContent>
            {testimonials.map((item, i) => <CarouselItem key={i} data-index={i}>
                <div className="flex flex-col items-center text-center px-4 md:px-8">
                  <div className="mb-8">
                    <Quote className="text-primary/20" strokeWidth={2} style={{
                  width: "4rem",
                  height: "4rem"
                }} aria-hidden="true" />
                  </div>

                  <blockquote className="font-default font-medium leading-[1.4] tracking-tight mb-10 max-w-4xl text-xl md:text-2xl italic text-primary-foreground">
                    "{item.quote}"
                  </blockquote>

                  <div className="flex flex-col items-center">
                    <div className="h-px w-12 bg-accent mb-6" />
                    <cite className="not-italic">
                      <span className="block font-default font-bold text-lg md:text-xl text-accent">
                        {item.name}
                      </span>
                      <span className="block font-default text-sm md:text-base mt-2 uppercase tracking-wider text-accent">
                        {item.role}
                      </span>
                    </cite>
                  </div>
                </div>
              </CarouselItem>)}
          </CarouselContent>
          <div className="hidden md:block">
            <CarouselPrevious className="left-[-60px] border-2 border-border hover:bg-accent hover:text-primary transition-colors rounded-lg" />
            <CarouselNext className="right-[-60px] border-2 border-border hover:bg-accent hover:text-primary transition-colors rounded-lg" />
          </div>
        </Carousel>
      </div>
    </section>;
}